import type {
  CheckInTemplate,
  Entry,
  Tag,
  Track,
  TrackValue,
  ValidationError,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { formatDate, formatTime } from '../../format/dates.js'
import { Chip } from '../../components/Chip.js'
import { TagPicker } from '../log/TagPicker.js'
import { UnsavedWorkGuard, useDiscardGuard } from '../../shell/DiscardGuard.js'
import { useServices } from '../../services/ServicesProvider.js'
import formStyles from '../log/EntryForm.module.css'
import styles from './Timeline.module.css'

/**
 * One entry, editable (T-306).
 *
 * Corrections are routine: a value entered in haste, a missing note, a
 * timestamp that was actually forty minutes earlier. Without editing, people
 * either abandon flawed entries or stop trusting the record — and a record
 * nobody trusts is not evidence.
 */
export function EntryDetailScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const entryId = useParams()['id'] ?? ''

  const [entry, setEntry] = useState<Entry | null>(null)
  const [template, setTemplate] = useState<CheckInTemplate | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [values, setValues] = useState<Record<string, TrackValue>>({})
  const [tagIds, setTagIds] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [at, setAt] = useState<Entry['at'] | null>(null)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)
  const [notFound, setNotFound] = useState(false)

  // One flag for the update-prompt deferral, the indicator and the discard
  // confirmation (T-204, T-808).
  const guard = useDiscardGuard(dirty)

  const load = useCallback(async () => {
    const [found, allTracks, allTags] = await Promise.all([
      repos.entries.get(entryId),
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setTags(allTags)
    if (!found) {
      setNotFound(true)
      return
    }
    setTemplate(
      found.templateId === undefined
        ? null
        : ((await repos.templates.get(found.templateId)) ?? null),
    )
    setEntry(found)
    setValues(Object.fromEntries(found.measurements.map((m) => [m.trackId, m.value])))
    setTagIds([...found.tagIds])
    setNote(found.note ?? '')
    setAt(found.at)
  }, [entryId, repos])

  useEffect(() => {
    void load()
  }, [load])

  /*
   * Writes, and reports whether it worked — without navigating. See the same
   * split in the track editor.
   */
  async function persist(): Promise<boolean> {
    if (!entry || !at) {
      return false
    }
    setErrors([])
    const measurements = Object.entries(values).map(([trackId, value]) => ({
      trackId,
      value,
      ...(entry.measurements.find((m) => m.trackId === trackId)?.note === undefined
        ? {}
        : { note: entry.measurements.find((m) => m.trackId === trackId)!.note! }),
    }))

    const result = await repos.entries.update(entry.id, {
      at,
      tagIds,
      measurements,
      ...(note.trim() === '' ? { note: undefined } : { note: note.trim() }),
    })

    if (!result.ok) {
      setErrors(result.errors)
      return false
    }
    setDirty(false)
    guard.allowLeaving()
    show({ message: 'Saved.' })
    return true
  }

  async function save() {
    if (await persist()) {
      void navigate('/timeline')
    }
  }

  async function createTag(name: string) {
    const result = await repos.tags.create(name)
    if (!result.ok) {
      show({
        message: result.errors[0]?.message ?? 'Could not add that.',
        tone: 'danger',
      })
      return
    }
    setTags(await repos.tags.list())
    setTagIds((current) => [...current, result.value.id])
    setDirty(true)
  }

  async function remove() {
    if (!entry) {
      return
    }
    const result = await repos.entries.remove(entry.id)
    if (!result.ok) {
      show({ message: 'Could not delete that.', tone: 'danger' })
      return
    }
    const removed = result.value
    void navigate('/timeline')
    /*
     * Undo, not a confirmation dialog.
     *
     * Confirmations get dismissed reflexively — the tap is muscle memory
     * before the sentence is read. Undo catches the mistake once it has been
     * *noticed*, which is when people actually notice. `restore` puts the entry
     * back with its original id, so anything holding a reference still points
     * at something that exists (T-107).
     */
    /*
     * Not the danger tone. That tone means "this failed" everywhere else in
     * the app, and this succeeded — the deletion is exactly what was asked for.
     * It read as an error the moment T-804 gave the tone real weight instead of
     * a 1px border nobody could see. The undo button is the emphasis a
     * destructive action needs; red text is the emphasis a mistake needs.
     */
    show({
      message: 'Entry deleted.',
      action: {
        label: 'Undo',
        onAction: () => {
          void repos.entries.restore(removed).then(() => {
            show({ message: 'Entry restored.' })
          })
        },
      },
    })
  }

  if (notFound) {
    return (
      /*
       * Not defensive trivia. Two tabs is enough to produce this, and so is a
       * bookmark from before a deletion — and the alternative to a screen is a
       * blank page with no way out of it.
       */
      <>
        <h1>Entry</h1>
        <EmptyState
          title="That entry is not here any more"
          body="It was probably deleted — in another tab, or before this link was opened. Nothing else has changed."
          action={
            <Button variant="primary" onClick={() => void navigate('/timeline')}>
              Back to the timeline
            </Button>
          }
        />
      </>
    )
  }

  if (!entry || !at) {
    return (
      <>
        <h1>Entry</h1>
        <p>Loading…</p>
      </>
    )
  }

  const measured = tracks.filter((track) => values[track.id] !== undefined)
  const addable = tracks.filter(
    (track) => !track.archived && values[track.id] === undefined,
  )
  /*
   * Measurements whose track no longer exists at all.
   *
   * Permanent deletion is only offered for tracks with no history, so this
   * should be unreachable — but "should be unreachable" is not a rendering
   * strategy, and a backup restored from a future version is enough to produce
   * one. Filtering them out silently would make a measurement invisible on the
   * one screen that exists to show what an entry holds.
   */
  const known = new Set(tracks.map((track) => track.id))
  const orphans = Object.entries(values).filter(([trackId]) => !known.has(trackId))

  return (
    <>
      <UnsavedWorkGuard guard={guard} what="This entry" onSave={persist} />

      <header className={styles['detailHeader']}>
        <div className={styles['detailHeading']}>
          {/*
            The check-in above, the time as the headline. An entry's identity is
            *when* it happened — that is what distinguishes it from every other
            entry of the same check-in, and it is what the data model is built
            around.
          */}
          <p className={styles['detailEyebrow']}>{template?.name ?? 'Entry'}</p>
          <h1 className={styles['detailTime']} data-testid="entry-when">
            {formatTime(at)}
          </h1>
          <p className={styles['detailDate']}>
            {formatDate(at)}
            {/*
              Always, not only when it differs from the device's. This is the
              detail screen and the stored zone is the point: it is what makes
              the time above mean anything after a flight.
            */}
            <span className={styles['detailZone']} data-testid="entry-zone">
              {at.timeZone}
            </span>
          </p>
        </div>
        <Button variant="danger" onClick={() => void remove()}>
          Delete
        </Button>
      </header>

      <div className={formStyles['form']}>
        <div className={formStyles['when']}>
          <input
            type="datetime-local"
            className={formStyles['datetime']}
            aria-label="Date and time"
            value={toLocalInput(at)}
            onChange={(event) => {
              const parsed = Date.parse(event.target.value)
              if (Number.isFinite(parsed)) {
                // Zone preserved: correcting the time does not relocate the
                // entry (ADR-0006).
                setAt({ epochMs: parsed, timeZone: at.timeZone })
                setDirty(true)
              }
            }}
          />
        </div>

        {measured.map((track) => (
          <section key={track.id} className={formStyles['slot']}>
            <div className={formStyles['slotHeader']}>
              <span className={formStyles['slotName']} id={`label-${track.id}`}>
                {track.name}
                {track.archived ? ' (archived)' : ''}
              </span>
              {track.unit !== undefined && (
                <span className={formStyles['slotUnit']}>{track.unit}</span>
              )}
            </div>
            <ValueInput
              track={track}
              value={values[track.id] ?? null}
              labelledBy={`label-${track.id}`}
              onChange={(next) => {
                setValues((current) => {
                  const updated = { ...current }
                  if (next === null) {
                    delete updated[track.id]
                  } else {
                    updated[track.id] = next
                  }
                  return updated
                })
                setDirty(true)
              }}
            />
            <button
              type="button"
              className={formStyles['skip']}
              onClick={() => {
                setValues((current) => {
                  const updated = { ...current }
                  delete updated[track.id]
                  return updated
                })
                setDirty(true)
              }}
            >
              Remove {track.name} from this entry
            </button>
          </section>
        ))}

        {orphans.map(([trackId, value]) => (
          <section key={trackId} className={formStyles['slot']}>
            <div className={formStyles['slotHeader']}>
              <span className={formStyles['slotName']}>Track no longer exists</span>
            </div>
            <p className={styles['orphanValue']} data-testid="orphan-measurement">
              {String(value)}
              <span className={styles['orphanId']}>{trackId}</span>
            </p>
            <button
              type="button"
              className={formStyles['skip']}
              onClick={() => {
                setValues((current) => {
                  const updated = { ...current }
                  delete updated[trackId]
                  return updated
                })
                setDirty(true)
              }}
            >
              Remove it from this entry
            </button>
          </section>
        ))}

        {addable.length > 0 && (
          <section className={formStyles['section']}>
            <h2 className={formStyles['sectionTitle']}>Add another measurement</h2>
            <div className={formStyles['tags']}>
              {addable.map((track) => (
                <Chip
                  key={track.id}
                  onClick={() => {
                    // Seeded from the track's own minimum where it has one, so
                    // the control opens somewhere valid rather than empty.
                    // A marker is only ever `true`, which is also the only
                    // thing worth seeding it with.
                    const seed: TrackValue =
                      track.kind.kind === 'numeric'
                        ? track.kind.min
                        : track.kind.kind === 'boolean' || track.kind.kind === 'marker'
                          ? true
                          : (track.kind.options[0]?.id ?? '')
                    setValues((current) => ({ ...current, [track.id]: seed }))
                    setDirty(true)
                  }}
                >
                  + {track.name}
                </Chip>
              ))}
            </div>
          </section>
        )}

        <section className={formStyles['section']}>
          <h2 className={formStyles['sectionTitle']}>Tags</h2>
          {/*
            The same picker the log flow uses, which carries "+ New tag".
            Without it this screen listed the tags that already existed and
            offered no way to make one — so an entry that turned out to need a
            tag could not get one without abandoning the edit, and on a database
            with no tags at all the section was an empty heading.
          */}
          <TagPicker
            tags={tags}
            selected={tagIds}
            onToggle={(tagId) => {
              setTagIds(
                tagIds.includes(tagId)
                  ? tagIds.filter((id) => id !== tagId)
                  : [...tagIds, tagId],
              )
              setDirty(true)
            }}
            onCreate={createTag}
          />
        </section>

        <section className={formStyles['section']}>
          <h2 className={formStyles['sectionTitle']}>Note</h2>
          <textarea
            className={formStyles['note']}
            aria-label="Note about this entry"
            value={note}
            onChange={(event) => {
              setNote(event.target.value)
              setDirty(true)
            }}
          />
        </section>

        {errors.length > 0 && (
          <div className={formStyles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={formStyles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className={formStyles['actions']}>
          <Button variant="primary" fullWidth onClick={() => void save()}>
            Save changes
          </Button>
        </div>
      </div>
    </>
  )
}

/** `<input type="datetime-local">` wants wall-clock text with no zone. */
function toLocalInput(at: Entry['at']): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: at.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at.epochMs)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}
